import Component from '@glimmer/component';
import type { FilterFieldControl } from '../filter-bar';
import type { Filter } from '../../../utils/filters';
import { eq } from '../../../utils/helpers.ts';

import {
  HdsFormTextInputField,
  HdsFormRadioGroup,
  HdsFormCheckboxGroup,
} from '@hashicorp/design-system-components/components';

export interface FilterControlSignature {
  Args: {
    filter: FilterFieldControl;
    appliedFilters: Record<string, Filter>;
  };

  Blocks: {
    default: [];
  };

  Element: HTMLElement;
}

export default class FilterControl extends Component<FilterControlSignature> {
  isCheckboxChecked = (name: string, value: string) => {
    const appliedFilter = this.args.appliedFilters[name];
    const rawValue = appliedFilter?.value?.value;
    if (!rawValue) {
      return false;
    }
    if (Array.isArray(rawValue)) {
      return rawValue.includes(value);
    }
    if (typeof rawValue === 'string') {
      return rawValue === value;
    }
    return false;
  };

  isLookbackChecked = (name: string, value: string) => {
    const appliedFilter = this.args.appliedFilters[name];
    if (!appliedFilter) {
      return false;
    } else {
      return appliedFilter.value.value === value;
    }
  };

  getValue = (name: string) => {
    return (this.args.appliedFilters?.[name]?.value?.value as string) ?? '';
  };

  getOperator = (name: string) => {
    return this.args.appliedFilters?.[name]?.operator ?? '';
  };

  <template>
    <div
      class="filter-control__wrapper"
      data-test-vault-reporting-filter-bar-control={{@filter.name}}
    >
      {{#if (eq @filter.type "lookback")}}
        <HdsFormRadioGroup @name="format" as |G|>
          {{#if @filter.label}}
            <G.Legend class="hds-typography-body-100-font-size">
              {{@filter.label}}
            </G.Legend>
          {{/if}}
          {{#each @filter.options as |option|}}
            <G.RadioField
              name={{@filter.name}}
              @value={{option.value}}
              checked={{this.isLookbackChecked @filter.name option.value}}
              as |F|
            ><F.Label>{{option.name}}</F.Label></G.RadioField>
          {{/each}}
        </HdsFormRadioGroup>
      {{else if (eq @filter.type "text")}}
        <div class="filter-bar__filters__text-filter-wrapper">
          <HdsFormTextInputField
            @type="text"
            name={{@filter.name}}
            @value={{this.getValue @filter.name}}
            as |F|
          >
            <F.Label>
              {{@filter.label}}
            </F.Label>
          </HdsFormTextInputField>
        </div>
      {{else if (eq @filter.type "single-select")}}
        <HdsFormRadioGroup @name="format" as |G|>
          {{#if @filter.label}}
            <G.Legend class="hds-typography-body-100-font-size">
              {{@filter.label}}
            </G.Legend>
          {{/if}}
          {{#each @filter.options as |option|}}
            <G.RadioField
              name={{@filter.name}}
              @value={{option.value}}
              checked={{eq (this.getValue @filter.name) option.value}}
              as |F|
            >
              <F.Label>{{option.name}}</F.Label>
            </G.RadioField>
          {{/each}}
        </HdsFormRadioGroup>
      {{else if (eq @filter.type "multi-select")}}
        <HdsFormCheckboxGroup as |G|>
          {{#if @filter.label}}
            <G.Legend>{{@filter.label}}</G.Legend>
          {{/if}}
          {{#each @filter.options as |option|}}
            <G.CheckboxField
              name={{@filter.name}}
              @value={{option.value}}
              checked={{this.isCheckboxChecked @filter.name option.value}}
              as |F|
            ><F.Label>{{option.name}}</F.Label></G.CheckboxField>
          {{/each}}
        </HdsFormCheckboxGroup>
      {{/if}}
    </div>
  </template>
}
